import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import './ProductCard.css';

function ProductCard({ product, variant = 'grid', showQuickView = false }) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  const detailUrl = product.url || `/shop/product/${product.slug || product.id}`;
  const wishlisted = isWishlisted(product.id);
  const maxQty = product.inStock ? Math.min(Math.max(product.stockQuantity, 1), 99) : 1;
  const qtyOptions = Array.from({ length: maxQty }, (_, i) => i + 1);
  const showPrice = product.priceRaw > 0;
  const quoteUrl = `/contact?product=${encodeURIComponent(product.name)}&sku=${encodeURIComponent(product.sku)}`;

  const handleAddToCart = (e) => {
    e.preventDefault();
    if (!product.inStock || !showPrice) return;
    addItem(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await toggleWishlist(product.id);
    } catch (err) {
      if (err.message === 'LOGIN_REQUIRED') {
        navigate('/login', { state: { from: { pathname: window.location.pathname } } });
      }
    }
  };

  return (
    <article className={`shop-product-card shop-product-card--${variant}`}>
      <div className="shop-product-card__image-wrap">
        <Link to={detailUrl} className="shop-product-card__image-link">
          <img
            src={product.image}
            alt={product.name}
            className="shop-product-card__image"
            loading="lazy"
            onError={(e) => {
              e.target.src = `https://via.placeholder.com/200x200?text=${encodeURIComponent(product.sku)}`;
            }}
          />
        </Link>
        {!product.inStock && (
          <span className="shop-product-card__badge shop-product-card__badge--oos">Out of Stock</span>
        )}
        <button
          type="button"
          className={`shop-product-card__wishlist ${wishlisted ? 'is-active' : ''}`}
          title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          onClick={handleWishlist}
          aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          {wishlisted ? '♥' : '♡'}
        </button>
      </div>

      <div className="shop-product-card__body">
        <h3 className="shop-product-card__name">
          <Link to={detailUrl}>{product.name}</Link>
        </h3>
        <p className="shop-product-card__sku">({product.sku})</p>

        <div className="shop-product-card__price-block">
          {product.hasDiscount && product.originalPrice && (
            <span className="shop-product-card__price-was">{product.originalPrice}</span>
          )}
          <span className="shop-product-card__price">
            {showPrice ? product.price : 'See Price'}
          </span>
        </div>

        <div className="shop-product-card__buy-row">
          <select
            id={`qty-${product.id}`}
            className="shop-product-card__qty-select"
            value={qty}
            disabled={!product.inStock}
            onChange={(e) => setQty(Number(e.target.value))}
            aria-label="Quantity"
          >
            {qtyOptions.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          {showPrice && product.inStock ? (
            <button
              type="button"
              className="shop-product-card__add-cart"
              onClick={handleAddToCart}
            >
              {added ? 'Added ✓' : 'Add to Cart'}
            </button>
          ) : (
            <Link to={detailUrl} className="shop-product-card__add-cart shop-product-card__add-cart--link">
              See Price
            </Link>
          )}
        </div>

        <Link to={quoteUrl} className="shop-product-card__quote">
          + Add to Quote
        </Link>

        {showQuickView && (
          <Link to={detailUrl} className="shop-product-card__details">View Details</Link>
        )}
      </div>
    </article>
  );
}

export default ProductCard;
